import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, Lightbulb, ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { Counter } from "@/components/motion";
import { CASE_STUDIES } from "@/data/site";

export default function CaseStudiesSection({ limit, showHeader = true }) {
  const items = limit ? CASE_STUDIES.slice(0, limit) : CASE_STUDIES;
  return (
    <section className="dq-container py-20 lg:py-28" data-testid="case-studies-section" id="case-studies">
      {showHeader && (
        <div className="mb-14 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeader
            eyebrow="Case studies"
            title="Real challenges,"
            highlight="measurable results"
            subtitle="See exactly how we turn business problems into growth."
          />
          <Link to="/case-studies" data-testid="case-view-all" className="btn-secondary shrink-0">
            All case studies <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <div className="space-y-8">
        {items.map((cs, i) => (
          <motion.article
            key={cs.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="grid overflow-hidden rounded-[2rem] border border-dq-line bg-white lg:grid-cols-2"
            data-testid={`case-study-${i}`}
          >
            <div className={`relative min-h-[280px] overflow-hidden ${i % 2 ? "lg:order-2" : ""}`}>
              <img src={cs.image} alt={`${cs.title} case study visual`} loading="lazy"
                className="absolute inset-0 h-full w-full object-cover" />
              <span className="absolute left-5 top-5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-dq-ink backdrop-blur">
                {cs.industry}
              </span>
            </div>
            <div className="p-8 lg:p-10">
              <h3 className="font-display text-2xl font-extrabold text-dq-ink">{cs.title}</h3>
              <div className="mt-6 space-y-5">
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-dq-orange">
                    <Target className="h-4 w-4" /> Challenge
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-dq-muted">{cs.challenge}</p>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-dq-orange">
                    <Lightbulb className="h-4 w-4" /> Solution
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-dq-muted">{cs.solution}</p>
                </div>
              </div>
              <div className="mt-7 grid grid-cols-3 gap-4 border-t border-dq-line pt-6">
                {cs.results.map((r) => (
                  <div key={r.label}>
                    <div className="font-display text-2xl font-extrabold text-dq-ink">
                      <Counter to={r.value} prefix={r.prefix || ""} suffix={r.suffix || ""} decimals={r.decimals || 0} />
                    </div>
                    <div className="mt-1 text-xs font-medium leading-tight text-dq-muted">{r.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
